import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AiPrompt, AiPromptMetadata } from '../../types/Ai';
import {
    requestAiPrompt,
    requestAiPromptMetadata,
    requestAiPrompts,
    requestAiPromptsMetadata,
    requestCreateAiPrompt,
    requestDeleteAiPrompt,
    requestUpdateAiPrompt,
} from '../ai/ai';
import { CreateAiPromptRequest, UpdateAiPromptRequest } from '../ai/types/Ai';

export const useAiPrompt = (promptId: number) => {
    return useQuery<AiPrompt>({
        queryKey: ['aiPrompt', promptId],
        queryFn: async ({ signal }) => {
            return (await requestAiPrompt(promptId, signal)).data;
        },
        enabled: promptId !== 0 && !Number.isNaN(promptId),
    });
};

export const useAiPrompts = () => {
    return useQuery<AiPrompt[]>({
        queryKey: ['aiPrompts'],
        queryFn: async ({ signal }) => (await requestAiPrompts(signal)).data,
    });
};

export const useCreateAiPrompt = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: CreateAiPromptRequest) => (await requestCreateAiPrompt(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiPrompts'] });
            queryClient.invalidateQueries({ queryKey: ['aiPromptMetadata'] });
        },
    });
};

export const useUpdateAiPrompt = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: UpdateAiPromptRequest) => (await requestUpdateAiPrompt(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiPrompts'] });
            queryClient.invalidateQueries({ queryKey: ['aiPrompt', data.promptId] });
            queryClient.invalidateQueries({ queryKey: ['aiPromptMetadata'] });
            queryClient.invalidateQueries({ queryKey: ['aiPromptMetadata', data.promptId] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlanItems'] });
        },
    });
};

export const useDeleteAiPrompt = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (promptId: number) => (await requestDeleteAiPrompt(promptId)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiPrompts'] });
            queryClient.invalidateQueries({ queryKey: ['aiPrompt', data.promptId] });
            queryClient.invalidateQueries({ queryKey: ['aiPromptMetadata'] });
            queryClient.invalidateQueries({ queryKey: ['aiPromptMetadata', data.promptId] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlanItems'] });
        },
    });
};

export const useAiPromptsMetadata = () => {
    return useQuery<AiPromptMetadata[]>({
        queryKey: ['aiPromptsMetadata'],
        queryFn: async ({ signal }) => (await requestAiPromptsMetadata(signal)).data,
    });
};

export const useAiPromptMetadata = (promptId: number) => {
    return useQuery<AiPromptMetadata>({
        queryKey: ['aiPromptMetadata', promptId],
        queryFn: async ({ signal }) => (await requestAiPromptMetadata(promptId, signal)).data,
    });
};
